/**
 * Extract variable indices from a WhatsApp template.
 * Counts text-type parameter slots per component:
 *   - Header/body: count text parameters where the text field contains {{N}} (variable placeholder)
 *   - Button components: count only parameters with type "text" and require_param === true
 *   - Footer: skipped (no variable substitution)
 * Media parameters (image, video, document) are excluded — they are not text variables.
 * Falls back to scanning template.body for {{N}} patterns when no components exist.
 *
 * @param {object|null|undefined} template — Template object from API
 * @returns {string[]} Sorted array of sequential variable indices (e.g. ['1', '2', '3'])
 */
export function extractTemplateVariables(template) {
  if (!template) return [];

  const found = new Set();
  let counter = 0;
  let hasComponentParams = false;

  if (Array.isArray(template.components)) {
    for (const comp of template.components) {
      if (!Array.isArray(comp.parameters)) continue;

      hasComponentParams = true;

      // Skip footer — no variable substitution
      if (comp.type?.toLowerCase() === 'footer') continue;

      const isButton = comp.type?.toLowerCase() === 'button';

      for (const param of comp.parameters) {
        // Only count text-type parameters (image/video/document are media placeholders)
        if (param.type !== 'text') continue;

        if (isButton) {
          // Button: only count if require_param === true (URL parameter substitution)
          if (param.require_param !== true) continue;
        } else {
          // Header/body: only count if text contains {{N}} (variable placeholder)
          // Static text like "Bienvenido" is not a variable even if type=text
          if (!/\{\{\d+\}\}/.test(param.text)) continue;
        }

        counter++;
        found.add(String(counter));
      }
    }
  }

  // Fallback: if no component params found, scan body for {{N}} patterns
  if (!hasComponentParams && typeof template.body === 'string') {
    const regex = /\{\{(\d+)\}\}/g;
    let match;
    while ((match = regex.exec(template.body)) !== null) {
      found.add(match[1]);
    }
  }

  return [...found].sort((a, b) => Number(a) - Number(b));
}
